import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileText, LogOut, Check, Loader2, Building2 } from "lucide-react";
import api, { fmtDate, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Messages from "@/pages/Messages";

/**
 * Restricted portal for client accounts. They can:
 *   - Upload a bank statement (routed to their accountant's data)
 *   - See a list of their previous uploads
 * Everything else in the app is off-limits (route guard redirects).
 */
export default function ClientPortal() {
  const { user, logout } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [accountant, setAccountant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [exiting, setExiting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [meRes, upRes] = await Promise.all([
        api.get("/clients/me"),
        api.get("/clients/me/uploads"),
      ]);
      setAccountant(meRes.data);
      setUploads(upRes.data.uploads);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== "client") {
    // Non-client users should never see this portal
    return <Navigate to="/app" replace />;
  }

  const onUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF only. Export a PDF from your bank's site.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/statements/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Statement uploaded — thanks!");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exitImpersonation = async () => {
    setExiting(true);
    try {
      await api.post("/clients/end-impersonation");
      window.location.href = "/app/clients";
    } catch (e) {
      toast.error("Could not return to your account");
      setExiting(false);
    }
  };

  if (loading) return <div className="p-8 text-ink-lighter">Loading…</div>;

  return (
    <div className="min-h-screen bg-paper" data-testid="client-portal">
      {accountant?.impersonating && (
        <div
          className="bg-ochre text-white px-6 py-2 flex items-center justify-between gap-3 text-sm"
          data-testid="impersonation-banner"
        >
          <div className="flex items-center gap-2">
            <span className="text-lg" aria-hidden="true">👤</span>
            <span>
              Viewing as <b>{user?.business_name || user?.name}</b> · uploads and messages will look identical to what this client sees.
            </span>
          </div>
          <button
            onClick={exitImpersonation}
            disabled={exiting}
            className="bg-white text-ochre font-medium px-3 py-1 rounded-md text-xs hover:bg-white/90 disabled:opacity-70 shrink-0"
            data-testid="exit-impersonation-btn"
          >
            {exiting ? "Returning…" : "Exit impersonation"}
          </button>
        </div>
      )}
      <header className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-forest text-white flex items-center justify-center">
              <Building2 className="w-4 h-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading text-lg font-medium">BizCashflow Pros</div>
              <div className="text-xs text-ink-lighter">Client portal</div>
            </div>
          </div>
          <button
            onClick={logout}
            className="text-sm text-ink-light hover:text-ink inline-flex items-center gap-1.5"
            data-testid="client-logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="overline">Welcome</div>
          <h1 className="font-heading text-3xl font-medium tracking-tight mt-1">
            Hi {user?.name?.split(" ")[0] || user?.business_name}
          </h1>
          {accountant?.accountant_business && (
            <p className="text-ink-light mt-1 text-sm">
              You&apos;re uploading statements to{" "}
              <span className="font-medium text-ink">{accountant.accountant_business}</span>.
            </p>
          )}
        </div>

        {/* Upload box */}
        <div className="card-surface p-6" data-testid="client-upload-card">
          <div className="overline mb-2">Upload a statement</div>
          <div className="font-heading text-lg font-medium mb-1">Add your latest bank statement (PDF)</div>
          <p className="text-sm text-ink-light">
            Grab it from your bank&apos;s website — usually under &ldquo;Statements&rdquo; or &ldquo;Documents&rdquo;. We&apos;ll extract the transactions automatically.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf"
              onChange={(e) => onUpload(e.target.files?.[0])}
              className="hidden"
              data-testid="client-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-forest inline-flex items-center gap-2"
              data-testid="client-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.75} />}
              {uploading ? "Uploading…" : "Choose PDF"}
            </button>
            <span className="text-xs text-ink-lighter">PDF only. Max 20MB.</span>
          </div>
        </div>

        {/* Upload history */}
        <div className="card-surface" data-testid="client-history">
          <div className="p-6 pb-3">
            <div className="overline mb-1">History</div>
            <div className="font-heading text-lg font-medium">Your uploads</div>
          </div>
          {uploads.length === 0 ? (
            <div className="px-6 pb-6 text-sm text-ink-lighter">No statements uploaded yet.</div>
          ) : (
            <div className="border-t border-border divide-y divide-border">
              {uploads.map((u) => {
                const received = !!u.received_at;
                return (
                  <div key={u.id} className="px-6 py-3 flex items-center gap-3 text-sm" data-testid={`upload-${u.id}`}>
                    <FileText className="w-4 h-4 text-forest shrink-0" strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.filename}</div>
                      <div className="text-xs text-ink-lighter num">
                        {fmtDate(u.period_start)} – {fmtDate(u.period_end)} · {u.transaction_count} transactions
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="text-forest num">+{fmtMoney(u.total_inflow)}</div>
                      <div className="text-ochre num">-{fmtMoney(u.total_outflow)}</div>
                    </div>
                    {/* Receipt status */}
                    <div
                      className="flex items-center gap-1.5 shrink-0"
                      data-testid={`upload-status-${u.id}`}
                      data-status={received ? "received" : "pending"}
                    >
                      {received ? (
                        <>
                          <span className="w-2.5 h-2.5 rounded-full bg-forest" />
                          <span className="text-[11px] text-forest font-medium">Received</span>
                        </>
                      ) : (
                        <>
                          <span className="relative flex w-2.5 h-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400" />
                          </span>
                          <span className="text-[11px] text-yellow-700 font-medium">Pending review</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Messages */}
        <div data-testid="client-messages-section">
          <div className="mb-3">
            <div className="overline">Messages</div>
            <div className="font-heading text-lg font-medium">Chat with your accountant</div>
          </div>
          <Messages embedded />
        </div>

        <div className="text-xs text-ink-lighter text-center pt-4">
          Powered by BizCashflow Pros
        </div>
      </main>
    </div>
  );
}
